/**
 * Demo mode — an in-memory stand-in for the Rails API.
 *
 * Enabled with `VITE_DEMO=1`. Every request from `client.ts` is answered from
 * the store below instead of going over the network, so the whole console and
 * booth can be driven with no backend, database or AI provider.
 *
 * It is deliberately stateful: creating an event, saving a draft and publishing
 * all mutate the store, so the UI behaves the way it will against the real API
 * rather than replaying canned responses.
 *
 * This file is the only place that fakes anything. Nothing else in the app
 * knows demo mode exists.
 */

import type {
  Blueprint, BlueprintDetail, DashboardPayload, EventDetail, EventSummary, GalleryPayload,
  GenerationHandle, GenerationStatusPayload, Order, OrderStatusPayload, OrderSummary,
  Organization, PrintJob, PrintQueuePayload, Session, SharePayload, SnapshotResult, User,
  EventMembership, EventPermission,
} from "../types/api";
import type { DraftConfig, EventConfig, PaymentMethod } from "../types/config";

export const DEMO = import.meta.env.VITE_DEMO === "1";

/** How long a fake generation "takes" before it resolves. */
const FAKE_GENERATION_MS = 4000;

// ── Placeholder imagery ─────────────────────────────────────────────────────
// Inline SVG data URLs so demo mode needs no network at all.

function placeholder(label: string, from: string, to: string, w = 800, h = 1000): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
    <defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="${from}"/><stop offset="100%" stop-color="${to}"/>
    </linearGradient></defs>
    <rect width="100%" height="100%" fill="url(#g)"/>
    <text x="50%" y="50%" fill="rgba(0,0,0,0.55)" font-family="Helvetica,Arial,sans-serif"
          font-size="${Math.round(w / 12)}" font-weight="bold" text-anchor="middle"
          dominant-baseline="middle">${label}</text>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

const GENERATED = placeholder("AI PHOTO", "#e8c578", "#372515");
const SNAPPED = placeholder("SNAPSHOT", "#4fe3d1", "#123b3a");
const SCENE_A = placeholder("STUDIO", "#e8c578", "#8a6a2f", 600, 750);
const SCENE_B = placeholder("STREET", "#4fe3d1", "#136b62", 600, 750);
const SCENE_C = placeholder("GOLDEN", "#f0b429", "#7a4a00", 600, 750);
const LOGO = placeholder("NEGUS", "#e8c578", "#e8c578", 400, 200);

// ── Store ───────────────────────────────────────────────────────────────────

const organization: Organization = {
  id: 1,
  name: "Jeba Studio",
  slug: "jeba-studio",
  generations_used: 220,
  generations_limit: 1000,
  generations_remaining: 780,
  generations_reset_at: new Date(Date.now() + 12 * 864e5).toISOString(),
  events_count: 3,
  users_count: 2,
  default_config: {
    event: { provider: "bfl", branding: { primary_color: "#d4f14f" } },
  },
};

const users: User[] = [
  { id: 1, email: "admin@jeba.ai", role: "org_admin", organization_id: 1,
    created_at: new Date(Date.now() - 90 * 864e5).toISOString(), invitation_pending: false },
  { id: 2, email: "producer@jeba.ai", role: "org_admin", organization_id: 1,
    created_at: new Date(Date.now() - 3 * 864e5).toISOString(),
    invited_at: new Date(Date.now() - 3 * 864e5).toISOString(), invitation_pending: true },
];

function baseConfig(name: string, slug: string): DraftConfig {
  return {
    event: {
      name, slug, event_type: "festival",
      provider: "bfl",
      branding: {
        primary_color: "#e8c578",
        secondary_color: "#372515",
        welcome_title: name,
        welcome_subtitle: "des des",
        welcome_tagline: "Welcome",
        welcome_cta: "Tap to start",
        welcome_layout: "split-visual",
        logo_url: LOGO,
        background_opacity: 0.12,
        text_colors: { headline: "#332010", subtitle: "#2c1305", tagline: "#ffffff", cta: "#ffffff" },
        text_sizes: { headline: 4, subtitle: 0.75, tagline: 1.5, cta: 1.75 },
        text_gap: 0,
      },
      gallery: { public: false },
      qr_code: { enabled: true, label: "Scan to download", size: 56, position: { x: 0.5, y: 0.9 } },
    },
    scenes: [
      { slug: "studio", name: "Studio Portrait", image_path: SCENE_A, sort_order: 0,
        description: "Clean studio lighting",
        default_prompt_template: "A cinematic studio portrait of the person at {{event_name}}." },
      { slug: "street", name: "Street Neon", image_path: SCENE_B, sort_order: 1,
        description: "Night city energy",
        default_prompt_template: "A neon-lit street portrait of the person at {{event_name}}." },
      { slug: "golden", name: "Golden Hour", image_path: SCENE_C, sort_order: 2,
        description: "Warm low sun",
        default_prompt_template: "A golden-hour portrait of the person at {{event_name}}." },
    ],
    participants: [],
    scenarios: [],
    moments: [
      { slug: "happy-hour", name: "Happy Hour", priority: 10, active: true,
        starts_at: new Date(Date.now() - 36e5).toISOString(),
        ends_at: new Date(Date.now() + 36e5).toISOString(),
        prompt_template: "A warm celebratory portrait, golden light, {{event_name}}.",
        scene_slugs: ["golden"] },
    ],
    workflow: { skip_participants: true, steps: [{ screen: "welcome" }] },
    assets: { copy: { print_button_label: "Print Photo", completion_message: "Your print is ready" } },
    hardware: { camera_type: "webcam", printer_type: "none", screen_resolution: "1920x1080" },
    snapshot: { enabled: true, filters: ["none", "mono", "film", "warm", "vivid"], free_retakes: 2 },
    // Capture is free; the guest pays to take the photo away.
    pricing: {
      currency: "ETB",
      ai_photo: { download: 5000, print: 7500, share: 0 },
      snapshot: { download: 2500, print: 5000, share: 0 },
    },
    experience: { blueprint_slug: "brand-activation" },
  };
}

// ── Per-event admins ────────────────────────────────────────────────────────
// Mirrors EventMembership::PERMISSIONS on the server.

const DEMO_PERMISSIONS: EventPermission[] = [
  "config", "publish", "pricing", "revenue", "gallery", "prints",
];
const DEMO_DEFAULT_PERMISSIONS: EventPermission[] = ["config", "publish", "gallery", "prints"];

const memberships: EventMembership[] = [];
let nextMembershipId = 1;

/**
 * Keeps `user.event_access` in step with the membership list.
 *
 * The real API derives one from the other on every request. Demo mode holds
 * both, so they have to be re-synced after any change — otherwise the console
 * grants or hides things based on a stale copy and demo mode stops behaving
 * like the thing it's standing in for.
 */
function syncEventAccess() {
  users.forEach((user) => {
    if (user.role !== "event_admin") { user.event_access = []; return; }
    user.event_access = memberships
      .filter((membership) => membership.user_id === user.id)
      .map((membership) => ({
        membership_id: membership.id,
        event_id: membership.event_id,
        event_name: events.find((event) => event.id === membership.event_id)?.name ?? "Event",
        permissions: membership.permissions,
      }));
  });
}

const blueprints: BlueprintDetail[] = [
  {
    id: 1, slug: "football-match", name: "Football Match", icon: "trophy", system: true,
    description: "Fans pick their club and get a photo tied to a specific fixture.",
    capture_modes: ["ai_photo_booth", "snapshot", "print_photo"], events_count: 1,
    taxonomy: { participant_singular: "Club", participant_prompt: "Choose your club" },
    vocabulary: {
      participant_singular: "Club", participant_plural: "Clubs",
      participant_prompt: "Choose your club", participant_hint: "Pick the side you're supporting today",
      scene_singular: "Moment", scene_plural: "Moments", scene_prompt: "Pick your moment",
      capture_verb: "Snap it", result_title: "You're in the squad",
    },
    custom_fields: [
      { key: "jersey_number", label: "Jersey number", type: "number", required: false,
        options: [], step: "capture", placeholder: "10", prompt_token: "{{field_jersey_number}}" },
    ],
    defaults: { services: { ai_photo_booth: true, photo_booth: false, print_photo: true }, workflow: { skip_participants: false }, snapshot: { enabled: true, filters: ["none", "vivid", "mono", "warm"], free_retakes: 2 } },
  },
  {
    id: 2, slug: "music-festival", name: "Music Festival", icon: "sparkles", system: true,
    description: "Stage-based lineup where guests align with an artist or stage.",
    capture_modes: ["ai_photo_booth", "snapshot", "print_photo"], events_count: 0,
    taxonomy: {}, vocabulary: { participant_prompt: "Which stage are you at?" },
    custom_fields: [], defaults: { services: { ai_photo_booth: true, photo_booth: false, print_photo: true }, workflow: { skip_participants: false }, snapshot: { enabled: true, filters: ["none", "vivid", "film", "noir", "bloom"], free_retakes: 3 } },
  },
  {
    id: 3, slug: "wedding", name: "Wedding", icon: "heart", system: true,
    description: "Two-sided celebration; guests pick a side and leave a message.",
    capture_modes: ["photo_booth", "snapshot", "print_photo"], events_count: 0,
    taxonomy: {}, vocabulary: { participant_prompt: "Whose side are you on?" },
    custom_fields: [
      { key: "message", label: "Message for the couple", type: "text", required: false,
        options: [], step: "result", prompt_token: "{{field_message}}" },
    ],
    defaults: { services: { ai_photo_booth: true, photo_booth: true, print_photo: true }, workflow: { skip_participants: false }, snapshot: { enabled: true, filters: ["none", "warm", "film", "mono"], free_retakes: 3 } },
  },
  {
    id: 4, slug: "brand-activation", name: "Brand Activation", icon: "sparkles", system: true,
    description: "Sponsor moment. No sides to pick — the brand's looks are the whole menu.",
    capture_modes: ["ai_photo_booth", "snapshot", "print_photo"], events_count: 2,
    taxonomy: { participants: false }, vocabulary: { scene_prompt: "Choose your look" },
    custom_fields: [], defaults: {},
  },
  {
    id: 5, slug: "nightclub", name: "Nightclub", icon: "sparkles", system: true,
    description: "Snapshot-first with filters — people are queuing and won't wait.",
    capture_modes: ["snapshot", "photo_booth", "print_photo"], events_count: 0,
    taxonomy: { participants: false }, vocabulary: { scene_prompt: "Pick your filter" },
    custom_fields: [], defaults: {},
  },
  {
    id: 6, slug: "esports", name: "Esports", icon: "trophy", system: true,
    description: "Team-based competitive gaming, with gamertags on the shot.",
    capture_modes: ["ai_photo_booth", "snapshot", "print_photo"], events_count: 0,
    taxonomy: {}, vocabulary: { participant_prompt: "Rep your team" },
    custom_fields: [
      { key: "gamertag", label: "Gamertag", type: "text", required: false,
        options: [], step: "capture", prompt_token: "{{field_gamertag}}" },
    ],
    defaults: {},
  },
  {
    id: 7, slug: "custom", name: "Custom Experience", icon: "sliders", system: true,
    description: "Neutral starting point — rename the vocabulary to match whatever you're running.",
    capture_modes: ["ai_photo_booth", "photo_booth", "snapshot", "print_photo"], events_count: 0,
    taxonomy: {}, vocabulary: {}, custom_fields: [], defaults: {},
  },
];

const SNAPSHOT_FILTERS = [
  { slug: "none", name: "Original", css: "none" },
  { slug: "mono", name: "Mono", css: "grayscale(1) contrast(1.08)" },
  { slug: "film", name: "Film", css: "sepia(0.28) contrast(1.12) saturate(0.9) brightness(1.02)" },
  { slug: "warm", name: "Warm", css: "saturate(1.25) sepia(0.15) brightness(1.05)" },
  { slug: "vivid", name: "Vivid", css: "saturate(1.6) contrast(1.15)" },
];

interface DemoOrder {
  reference: string;
  status: Order["status"];
  amount_cents: number;
  currency: string;
  checkout_url: string;
  snapshotId: number;
  createdAt: number;
  event_id: number;
}

const orders: DemoOrder[] = [];
let nextSnapshotId = 5000;

interface DemoSnapshot {
  id: number;
  event_id: number;
  token: string;
  filter_slug: string;
  status: string;
  created_at: string;
}

const snapshots: DemoSnapshot[] = [];

interface DemoEvent extends EventDetail {
  configData: DraftConfig;
  draftData: DraftConfig | null;
}

function makeEvent(id: number, name: string, slug: string, status: EventSummary["status"]): DemoEvent {
  const config = baseConfig(name, slug);
  return {
    id, name, slug, status, event_type: "festival",
    created_at: new Date(Date.now() - id * 864e5).toISOString(),
    starts_at: null, ends_at: null,
    scenes_count: config.scenes?.length ?? 0,
    participants_count: 0,
    moments_count: config.moments?.length ?? 0,
    generations_count: 40 - id * 5,
    draft_config_present: false,
    photo_booth: true, ai_photo_booth: true, print_photo: true, snapshot: true,
    blueprint_slug: "brand-activation",
    hardware: config.hardware,
    configData: config,
    draftData: null,
  };
}

const events: DemoEvent[] = [
  makeEvent(1, "Negus Malt", "negus-malt", "published"),
  makeEvent(2, "Habesha", "habesha", "published"),
  makeEvent(3, "UCL Final 2025", "ucl-final-2025", "draft"),
];

interface DemoGeneration {
  id: number;
  event_id: number;
  token: string;
  created_at: string;
  startedAt: number;
  status: GenerationStatusPayload["status"];
}

const generations: DemoGeneration[] = Array.from({ length: 18 }, (_, index) => ({
  id: 1000 + index,
  event_id: (index % 3) + 1,
  token: `demo${index}${"a".repeat(24)}`.slice(0, 32),
  created_at: new Date(Date.now() - index * 36e5).toISOString(),
  startedAt: 0,
  status: "completed" as const,
}));

const printJobs: PrintJob[] = [
  { id: 501, status: "completed", copies: 1, adapter: "null", printer_type: "none",
    generation_id: 1000, event_id: 1, event_name: "Negus Malt", print_asset_url: GENERATED,
    error: null, created_at: new Date(Date.now() - 12e5).toISOString(),
    completed_at: new Date(Date.now() - 11e5).toISOString() },
  { id: 502, status: "failed", copies: 1, adapter: "cups", printer_type: "dnp",
    printer_name: "DNP-DS620", generation_id: 1001, event_id: 1, event_name: "Negus Malt",
    print_asset_url: null, error: "Printer reported out of paper",
    created_at: new Date(Date.now() - 6e5).toISOString(), completed_at: null },
];

let nextEventId = 100;
let nextGenerationId = 2000;

// ── Serialisation ───────────────────────────────────────────────────────────

function summary(event: DemoEvent): EventSummary {
  const source = event.draftData ?? event.configData;
  return {
    id: event.id, name: event.name, slug: event.slug, event_type: event.event_type,
    status: event.status, created_at: event.created_at,
    starts_at: event.starts_at, ends_at: event.ends_at,
    scenes_count: source.scenes?.length ?? 0,
    participants_count: source.participants?.length ?? 0,
    moments_count: source.moments?.length ?? 0,
    generations_count: event.generations_count,
    draft_config_present: event.draftData !== null,
    photo_booth: event.photo_booth, ai_photo_booth: event.ai_photo_booth,
    print_photo: event.print_photo,
  };
}

function detail(event: DemoEvent): EventDetail {
  return {
    ...summary(event),
    hardware: (event.draftData ?? event.configData).hardware ?? null,
    config: event.configData as unknown as Record<string, unknown>,
    draft_config: event.draftData as unknown as Record<string, unknown> | null,
  };
}

/** Mirrors what EventConfigSerializer returns for the booth. */
function boothConfig(event: DemoEvent, preview: boolean): EventConfig {
  const source = (preview && event.draftData) || event.configData;
  const scenes = source.scenes ?? [];
  const moments = source.moments ?? [];
  const now = Date.now();

  const active = moments.find((moment) => {
    if (moment.active === false) return false;
    const starts = moment.starts_at ? new Date(moment.starts_at).getTime() : null;
    const ends = moment.ends_at ? new Date(moment.ends_at).getTime() : null;
    return (starts === null || starts <= now) && (ends === null || ends > now);
  }) ?? null;

  return {
    event: {
      name: event.name, slug: event.slug, event_type: event.event_type,
      status: event.status,
      service_types: [
        ...(event.ai_photo_booth ? ["ai_photo_booth" as const] : []),
        ...(event.photo_booth ? ["photo_booth" as const] : []),
        ...(event.print_photo ? ["print_photo" as const] : []),
      ],
      ...source.event,
      hardware: source.hardware,
    },
    scenes,
    participants: source.participants ?? [],
    scenarios: source.scenarios ?? [],
    moments,
    active_moment: active,
    workflow: { skip_participants: true, steps: [], ...source.workflow },
    assets: source.assets,
    experience: {
      blueprint: blueprintRef(source.experience?.blueprint_slug),
      vocabulary: {
        ...(blueprints.find((b) => b.slug === source.experience?.blueprint_slug)?.vocabulary ?? {}),
        ...(source.experience?.taxonomy ?? {}),
      },
      fields: blueprints.find((b) => b.slug === source.experience?.blueprint_slug)?.custom_fields ?? [],
      capture_modes: ["ai_photo_booth", "snapshot", "print_photo"],
      participant_selection: (source.participants ?? []).length > 0,
    },
    snapshot: {
      enabled: event.snapshot ?? true,
      filters: SNAPSHOT_FILTERS.filter((f) =>
        (source.snapshot?.filters?.length ? source.snapshot.filters : ["none", "mono", "film", "warm", "vivid"]).includes(f.slug)),
      layouts: source.snapshot?.layouts?.length ? source.snapshot.layouts : ["single"],
      countdown_seconds: source.snapshot?.countdown_seconds ?? 3,
      free_retakes: source.snapshot?.free_retakes ?? 2,
    },
    pricing: pricingFor(source),
  };
}

/** Mirrors Pricing.as_json on the server. */
function pricingFor(source: DraftConfig) {
  const configured = source.pricing ?? {};
  const enabled = organization.payments_enabled ?? true;
  const cell = (cents: number) => ({ cents, payable: enabled && cents > 0 });

  return {
    enabled,
    methods: ["phone", "qr"] as PaymentMethod[],
    test_mode: true,
    currency: configured.currency ?? "ETB",
    grid: {
      ai_photo: {
        download: cell(configured.ai_photo?.download ?? 0),
        print: cell(configured.ai_photo?.print ?? 0),
        share: cell(configured.ai_photo?.share ?? 0),
      },
      snapshot: {
        download: cell(configured.snapshot?.download ?? 0),
        print: cell(configured.snapshot?.print ?? 0),
        share: cell(configured.snapshot?.share ?? 0),
      },
    },
  };
}

/** Entitlement summary for one photo, matching Entitlements.summary. */
function actionStatesFor(kind: "ai_photo" | "snapshot", token: string) {
  const grid = pricingFor(events[0]!.draftData ?? events[0]!.configData).grid[kind];
  const currency = "ETB";

  return (["download", "print", "share"] as const).reduce((acc, action) => {
    const cell = grid[action];
    acc[action] = {
      cents: cell.cents,
      currency,
      payable: cell.payable,
      granted: !cell.payable || paidActions.has(`${token}:${action}`),
    };
    return acc;
  }, {} as Record<"download" | "print" | "share", {
    cents: number; currency: string; payable: boolean; granted: boolean;
  }>);
}

/** Which (photo × action) pairs have been paid for in this demo session. */
const paidActions = new Set<string>();

function blueprintRef(slug?: string) {
  const found = blueprints.find((item) => item.slug === slug);
  return found ? { slug: found.slug, name: found.name, icon: found.icon } : null;
}

const session: Session = {
  token: "demo-access-token",
  refresh_token: "demo-refresh-token",
  expires_in: 86400,
  user: users[0]!,
  organization,
};

Object.assign(organization, {
  white_label: { name: "Jeba Studio" },
  subdomain: "demo",
  booth_host: "demo.jeba.app",
  currency: "ETB",
  payments_enabled: true,
  payment_provider: "telebirr",
  configured_payment_keys: ["app_id", "short_code"],
});

// ── Router ──────────────────────────────────────────────────────────────────

const delay = (ms = 220) => new Promise((resolve) => setTimeout(resolve, ms));

function findEvent(id: string | number): DemoEvent | undefined {
  return events.find((event) => String(event.id) === String(id));
}

function findEventBySlug(slug: string): DemoEvent | undefined {
  return events.find((event) => event.slug === decodeURIComponent(slug));
}

/**
 * Answers a request from the store.
 *
 * Returns `undefined` for anything unrecognised so the caller can fall through
 * to a real network request rather than silently swallowing it.
 */
export async function demoRequest(
  path: string,
  method: string,
  body: unknown,
): Promise<unknown> {
  await delay();

  const [rawPath] = path.split("?");
  const url = rawPath ?? "";
  const query = new URLSearchParams(path.includes("?") ? path.slice(path.indexOf("?") + 1) : "");
  const segments = url.split("/").filter(Boolean); // ["api", ...]
  const payload = body as Record<string, unknown> | undefined;

  // ── Auth ──
  if (url === "/api/auth/login") return session;
  if (url === "/api/auth/register") return session;
  if (url === "/api/auth/refresh") return session;
  if (url === "/api/auth/me") return { user: session.user, organization };
  if (url === "/api/auth/logout") return { message: "Logged out" };
  if (url.startsWith("/api/auth/password")) return { message: "Demo mode — no email is sent." };

  // ── Public booth ──
  if (segments[1] === "events" && segments[3] === "config") {
    const event = findEventBySlug(segments[2]!);
    if (!event) throw new DemoNotFound("No event lives at that link");
    return boothConfig(event, query.get("preview") === "true");
  }

  if (segments[1] === "events" && segments[3] === "generate") {
    const event = findEventBySlug(segments[2]!);
    if (!event) throw new DemoNotFound("Event not found");
    const generation: DemoGeneration = {
      id: nextGenerationId++,
      event_id: event.id,
      token: `demo-${Math.random().toString(36).slice(2, 12)}`,
      created_at: new Date().toISOString(),
      startedAt: Date.now(),
      status: "processing",
    };
    generations.unshift(generation);
    const handle: GenerationHandle = {
      id: generation.id, status: "pending", public_token: generation.token,
      provider: "bfl", status_url: `/api/events/${event.slug}/generations/${generation.id}/status`,
    };
    return handle;
  }

  if (segments[1] === "events" && segments[3] === "generations" && segments[5] === "status") {
    const generation = generations.find((item) => String(item.id) === segments[4]);
    if (!generation) throw new DemoNotFound("Generation not found");

    // Resolve after a plausible wait so the polling screen is exercised.
    const elapsed = Date.now() - generation.startedAt;
    if (generation.status === "processing" && elapsed > FAKE_GENERATION_MS) {
      generation.status = "completed";
    }

    const done = generation.status === "completed";
    const status: GenerationStatusPayload = {
      id: generation.id,
      status: done ? "completed" : "processing",
      image_url: done ? GENERATED : null,
      public_token: generation.token,
      share_url: `/s/${generation.token}`,
      provider: "bfl",
      error: null,
      created_at: generation.created_at,
      completed_at: done ? new Date().toISOString() : null,
    };
    return status;
  }

  if (segments[1] === "events" && segments[3] === "generations") {
    const event = findEventBySlug(segments[2]!);
    if (!event) throw new DemoNotFound("Event not found");
    return generations
      .filter((item) => item.event_id === event.id && item.status === "completed")
      .map((item) => ({
        id: item.id, provider: "bfl", created_at: item.created_at,
        image_url: GENERATED, share_url: `/s/${item.token}`,
        prompt_used: "A cinematic studio portrait…",
      }));
  }

  if (segments[1] === "generations" && segments.length === 3) {
    const generation = generations.find((item) => item.token === decodeURIComponent(segments[2]!));
    if (!generation || generation.status !== "completed") throw new DemoNotFound("Photo not found");
    const event = events.find((item) => item.id === generation.event_id);
    const actions = actionStatesFor("ai_photo", generation.token);
    const unlocked = actions.download.granted || actions.share.granted;
    const share: SharePayload = {
      token: generation.token,
      kind: "ai_photo",
      event_name: event?.name ?? "Jeba",
      event_slug: event?.slug ?? "demo",
      created_at: generation.created_at,
      // Demo mode has no image pipeline, so preview and original are the same
      // picture. On the real API the preview is a 720px variant — that's what
      // makes the paywall mean anything on a phone.
      preview_url: GENERATED,
      image_url: unlocked ? GENERATED : null,
      actions,
      currency: actions.download.currency,
      payment_methods: ["telebirr"],
      test_mode: true,
    };
    return share;
  }

  // ── Snapshot ──
  if (segments[1] === "events" && segments[3] === "snapshots" && method === "POST") {
    const event = findEventBySlug(segments[2]!);
    if (!event) throw new DemoNotFound("Event not found");

    const snapshot: DemoSnapshot = {
      id: nextSnapshotId++,
      event_id: event.id,
      token: `snap-${Math.random().toString(36).slice(2, 12)}`,
      filter_slug: String(payload?.filter_slug ?? "none"),
      status: "ready",
      created_at: new Date().toISOString(),
    };
    snapshots.unshift(snapshot);

    const result: SnapshotResult = {
      id: snapshot.id,
      status: "ready",
      filter_slug: snapshot.filter_slug,
      public_token: snapshot.token,
      share_url: `/s/${snapshot.token}`,
      // Shown clean — paying is what gets it out of the booth.
      image_url: SNAPPED,
      created_at: snapshot.created_at,
      actions: actionStatesFor("snapshot", snapshot.token),
    };
    return result;
  }

  if (segments[1] === "events" && segments[3] === "snapshots") {
    const snapshot = snapshots.find((item) => String(item.id) === segments[4]);
    if (!snapshot) throw new DemoNotFound("Snapshot not found");
    return {
      id: snapshot.id, status: snapshot.status, filter_slug: snapshot.filter_slug,
      public_token: snapshot.token, share_url: `/s/${snapshot.token}`,
      image_url: SNAPPED, created_at: snapshot.created_at,
      actions: actionStatesFor("snapshot", snapshot.token),
    };
  }

  // ── Purchases ──
  if (segments[1] === "events" && segments[3] === "purchases" && method === "POST") {
    const action = String(payload?.purchase_action ?? "download") as "download" | "print" | "share";
    const token = snapshots.find((item) => item.id === Number(payload?.snapshot_id))?.token
      ?? generations.find((item) => item.id === Number(payload?.generation_id))?.token
      ?? String(payload?.public_token ?? "");
    const kind = payload?.snapshot_id ? "snapshot" : "ai_photo";
    const state = actionStatesFor(kind, token)[action];

    if (!state.payable || state.granted) {
      paidActions.add(`${token}:${action}`);
      return { public_token: token, kind, action, granted: true, order: null };
    }

    const order: DemoOrder = {
      reference: `JB-DEMO-${Math.random().toString(36).slice(2, 8).toUpperCase()}`,
      status: "awaiting_payment",
      amount_cents: state.cents,
      currency: state.currency,
      // In demo the QR points at a local page that settles the order, so the
      // whole scan-and-pay flow can be walked on one machine.
      checkout_url: `${window.location.origin}/pay/${token}::${action}`,
      snapshotId: Number(payload?.snapshot_id ?? 0),
      createdAt: Date.now(),
      event_id: events[0]!.id,
    };
    orders.unshift(order);

    // A phone number switches the flow to a simulated USSD push, so the
    // "check your phone" screen can be exercised without a merchant account.
    const pushed = Boolean(payload?.payer_phone);

    return {
      public_token: token, kind, action, granted: false,
      order: {
        reference: order.reference, status: order.status, amount_cents: order.amount_cents,
        currency: order.currency,
        checkout_url: pushed ? null : order.checkout_url,
        mode: pushed ? ("push" as const) : ("redirect" as const),
        instructions: pushed
          ? `Simulated push. Open ${order.checkout_url} in another tab to settle it.`
          : null,
        expires_at: new Date(Date.now() + 9e5).toISOString(), paid: false,
      },
    };
  }

  // Test-mode settlement, mirroring the real endpoint.
  if (segments[1] === "events" && segments[3] === "purchases" && segments[5] === "simulate") {
    const reference = decodeURIComponent(segments[4] ?? "");
    const order = orders.find((item) => item.reference === reference);
    if (order) {
      order.status = "paid";
      const snapshot = snapshots.find((item) => item.id === order.snapshotId);
      const match = order.checkout_url.split("/pay/")[1] ?? "";
      const [token, action] = decodeURIComponent(match).split("::");
      if (token && action) paidActions.add(`${token}:${action}`);
      else if (snapshot) paidActions.add(`${snapshot.token}:download`);
    }
    return { reference, status: "paid", paid: true, action: "download" };
  }

  if (segments[1] === "events" && segments[3] === "purchases") {
    const token = decodeURIComponent(segments[4] ?? "");
    const kind = token.startsWith("snap-") ? "snapshot" : "ai_photo";
    return { public_token: token, kind, actions: actionStatesFor(kind, token) };
  }

  // The gated download.
  if (segments[1] === "events" && segments[3] === "download") {
    const token = decodeURIComponent(segments[4] ?? "");
    if (!paidActions.has(`${token}:download`)) {
      throw new DemoUnsupported("This download hasn't been paid for.");
    }
    return { image_url: SNAPPED };
  }

  // ── Payments ──
  if (segments[1] === "payments" && segments[2] === "orders") {
    const order = orders.find((item) => item.reference === decodeURIComponent(segments[3]!));
    if (!order) throw new DemoNotFound("Order not found");

    const snapshot = snapshots.find((item) => item.id === order.snapshotId);
    const status: OrderStatusPayload = {
      reference: order.reference,
      status: order.status,
      amount_cents: order.amount_cents,
      currency: order.currency,
      checkout_url: order.checkout_url,
      paid: order.status === "paid",
      expires_at: null,
      purchasable_type: "Snapshot",
      public_token: snapshot?.token ?? null,
      image_url: order.status === "paid" ? SNAPPED : null,
    };
    return status;
  }

  // Demo-only: the QR target settles the order, standing in for telebirr.
  if (segments[1] === "demo" && segments[2] === "pay") {
    // The QR encodes "<token>::<action>".
    const [token, action] = decodeURIComponent(segments[3] ?? "").split("::");
    if (token && action) paidActions.add(`${token}:${action}`);
    orders.filter((order) => order.status === "awaiting_payment")
          .forEach((order) => { order.status = "paid"; });
    return { paid: true };
  }

  // ── Printing ──
  if (segments[1] === "events" && segments[3] === "print") {
    const event = findEventBySlug(segments[2]!);
    const job: PrintJob = {
      id: 900 + printJobs.length, status: "ready", copies: 1, adapter: "null",
      printer_type: "none", generation_id: Number(payload?.generation_id ?? 0),
      event_id: event?.id, event_name: event?.name,
      print_asset_url: GENERATED, error: null,
      created_at: new Date().toISOString(), completed_at: null,
    };
    printJobs.unshift(job);
    return job;
  }

  if (segments[1] === "events" && segments[3] === "print_jobs") {
    const job = printJobs.find((item) => String(item.id) === segments[4]);
    if (!job) throw new DemoNotFound("Print job not found");
    if (segments[5] === "complete") job.status = "completed";
    if (segments[5] === "claim") job.status = "printing";
    return job;
  }

  // ── Admin: blueprints, orders, snapshots ──
  if (url === "/api/admin/blueprints") {
    if (method === "POST") return blueprints[blueprints.length - 1];
    return blueprints.map(({ taxonomy, vocabulary, custom_fields, defaults, ...rest }) => {
      void taxonomy; void vocabulary; void custom_fields; void defaults;
      return rest as Blueprint;
    });
  }

  if (segments[2] === "blueprints" && segments[3]) {
    const blueprint = blueprints.find((item) => String(item.id) === segments[3]);
    if (!blueprint) throw new DemoNotFound("Blueprint not found");
    return blueprint;
  }

  if (url === "/api/admin/orders/summary") {
    const paid = orders.filter((order) => order.status === "paid");
    const summary: OrderSummary = {
      currency: "ETB",
      gross_cents: paid.reduce((total, order) => total + order.amount_cents, 0) + 47500,
      paid_count: paid.length + 19,
      by_status: {
        paid: paid.length + 19,
        awaiting_payment: orders.filter((o) => o.status === "awaiting_payment").length,
        failed: 2,
        expired: 1,
      },
      last_7_days: {},
      top_events: { "Negus Malt": 32500, Habesha: 15000 },
    };
    return summary;
  }

  if (url === "/api/admin/orders") {
    const seeded: Order[] = Array.from({ length: 8 }, (_, index) => ({
      id: 700 + index,
      reference: `JB-260802-DEMO${index}${"X".repeat(4)}`.slice(0, 20),
      status: (index === 2 ? "failed" : index === 5 ? "expired" : "paid") as Order["status"],
      amount_cents: 2500,
      currency: "ETB",
      formatted_amount: "25.00 ETB",
      provider: "telebirr",
      provider_reference: `TB-${9000 + index}`,
      event_id: (index % 2) + 1,
      event_name: index % 2 === 0 ? "Negus Malt" : "Habesha",
      purchasable_type: "Snapshot",
      payer_phone: "+2519••••••" + index,
      failure_reason: index === 2 ? "Payer cancelled in telebirr" : null,
      created_at: new Date(Date.now() - index * 72e5).toISOString(),
      paid_at: index === 2 || index === 5 ? null : new Date(Date.now() - index * 72e5).toISOString(),
    }));

    const live: Order[] = orders.map((order, index) => ({
      id: 800 + index,
      reference: order.reference,
      status: order.status,
      amount_cents: order.amount_cents,
      currency: order.currency,
      formatted_amount: `${(order.amount_cents / 100).toFixed(2)} ${order.currency}`,
      provider: "telebirr",
      provider_reference: null,
      event_id: order.event_id,
      event_name: events.find((event) => event.id === order.event_id)?.name ?? "Event",
      purchasable_type: "Snapshot",
      payer_phone: null,
      failure_reason: null,
      created_at: new Date(order.createdAt).toISOString(),
      paid_at: order.status === "paid" ? new Date().toISOString() : null,
    }));

    const all = [...live, ...seeded];
    return {
      orders: all,
      pagination: { current_page: 1, per_page: 25, total_count: all.length, total_pages: 1 },
    };
  }

  if (url === "/api/admin/snapshots") {
    return {
      snapshots: snapshots.map((snapshot) => ({
        id: snapshot.id, status: snapshot.status, event_id: snapshot.event_id,
        event_name: events.find((event) => event.id === snapshot.event_id)?.name ?? "Event",
        filter_slug: snapshot.filter_slug, guest_fields: {},
        image_url: SNAPPED, share_url: `/s/${snapshot.token}`, created_at: snapshot.created_at,
      })),
      pagination: { current_page: 1, per_page: 24, total_count: snapshots.length, total_pages: 1 },
    };
  }

  // ── Admin ──
  if (url === "/api/admin/dashboard") {
    const dashboard: DashboardPayload = {
      organization,
      stats: {
        active_events: events.filter((event) => event.status === "published").length,
        draft_events: events.filter((event) => event.status === "draft").length,
        archived_events: events.filter((event) => event.status === "archived").length,
      },
      generation_health: { last_24h: 24, in_flight: 1, failed: 1, completed: 22 },
      print_health: { completed: 12, failed: 1 },
      recent_generations: generations.slice(0, 5).map((item) => ({
        id: item.id,
        event_name: events.find((event) => event.id === item.event_id)?.name ?? "Event",
        event_slug: events.find((event) => event.id === item.event_id)?.slug ?? "event",
        prompt_used: "Photorealistic image of a large outdoor LED billboard in Addis Ababa at dusk…",
        provider: "bfl",
        created_at: item.created_at,
        image_url: GENERATED,
      })),
    };
    return dashboard;
  }

  if (url === "/api/admin/generations") {
    const perPage = Number(query.get("per_page") ?? 24);
    const page = Number(query.get("page") ?? 1);
    const items = generations.filter((item) => item.status === "completed");
    const gallery: GalleryPayload = {
      generations: items.slice((page - 1) * perPage, page * perPage).map((item) => ({
        id: item.id, event_id: item.event_id,
        event_name: events.find((event) => event.id === item.event_id)?.name ?? "Event",
        event_slug: events.find((event) => event.id === item.event_id)?.slug ?? "event",
        prompt_used: "A cinematic studio portrait of the person, dramatic rim lighting.",
        provider: "bfl", created_at: item.created_at, image_url: GENERATED,
      })),
      events: events.map((event) => ({ id: event.id, name: event.name })),
      providers: ["bfl", "openai"],
      pagination: {
        current_page: page, per_page: perPage, total_count: items.length,
        total_pages: Math.max(1, Math.ceil(items.length / perPage)),
      },
    };
    return gallery;
  }

  if (url === "/api/admin/generations/export") {
    throw new DemoUnsupported("ZIP export needs the real API — there's nothing to download in demo mode.");
  }

  if (url === "/api/admin/print_jobs") {
    const queue: PrintQueuePayload = {
      print_jobs: printJobs,
      summary: { completed: 12, failed: 1, ready: 1 },
      pagination: { current_page: 1, per_page: 25, total_count: printJobs.length, total_pages: 1 },
    };
    return queue;
  }

  if (segments[2] === "print_jobs" && segments[4] === "retry") {
    const job = printJobs.find((item) => String(item.id) === segments[3]);
    if (job) { job.status = "queued"; job.error = null; }
    return job;
  }

  if (url === "/api/admin/users") {
    if (method === "POST") {
      const created: User = {
        id: users.length + 1,
        email: String((payload?.user as Record<string, unknown> | undefined)?.email ?? "new@jeba.ai"),
        role: (((payload?.user as Record<string, unknown> | undefined)?.role as User["role"]) ?? "org_admin"),
        organization_id: 1,
        event_access: [],
        created_at: new Date().toISOString(),
        // Created with a password, so it works immediately — no invitation.
        invited_at: null, invitation_pending: false,
      };
      users.push(created);
      return created;
    }
    return users;
  }

  if (segments[2] === "users" && segments[3]) {
    const id = Number(segments[3]);
    if (method === "PATCH") {
      const user = users.find((item) => item.id === id);
      if (user) Object.assign(user, payload?.user ?? {});
      return user;
    }
    if (method === "DELETE") {
      const index = users.findIndex((user) => user.id === id);
      if (index >= 0) users.splice(index, 1);
      return { message: "User removed" };
    }
    return users.find((user) => user.id === id);
  }

  // /api/admin/events/:id/memberships[/:membershipId]
  if (segments[2] === "events" && segments[4] === "memberships") {
    const eventId = Number(segments[3]);
    const membershipId = segments[5] ? Number(segments[5]) : null;

    if (method === "POST") {
      const userId = Number(payload?.user_id);
      const user = users.find((item) => item.id === userId);
      const created: EventMembership = {
        id: nextMembershipId++,
        event_id: eventId,
        user_id: userId,
        email: user?.email ?? "unknown@jeba.ai",
        permissions: (payload?.permissions as EventPermission[]) ?? [...DEMO_DEFAULT_PERMISSIONS],
        invitation_pending: user?.invitation_pending ?? false,
        created_at: new Date().toISOString(),
      };
      memberships.push(created);
      syncEventAccess();
      return created;
    }

    if (membershipId && method === "PATCH") {
      const membership = memberships.find((item) => item.id === membershipId);
      if (membership) membership.permissions = (payload?.permissions as EventPermission[]) ?? [];
      syncEventAccess();
      return membership;
    }

    if (membershipId && method === "DELETE") {
      const index = memberships.findIndex((item) => item.id === membershipId);
      if (index >= 0) memberships.splice(index, 1);
      syncEventAccess();
      return { message: "Access removed" };
    }

    return {
      permissions: DEMO_PERMISSIONS,
      default_permissions: DEMO_DEFAULT_PERMISSIONS,
      memberships: memberships.filter((item) => item.event_id === eventId),
    };
  }

  if (url === "/api/admin/organizations") return [organization];
  if (segments[2] === "organizations" && segments[3]) {
    if (method === "PATCH" && payload?.organization) {
      Object.assign(organization, payload.organization);
    }
    return organization;
  }

  if (url === "/api/admin/assets/upload") {
    return {
      url: GENERATED, signed_id: `demo-asset-${Date.now()}`,
      filename: "demo-upload.png", byte_size: 12345, content_type: "image/png",
    };
  }

  // ── Admin events ──
  if (url === "/api/admin/events") {
    if (method === "POST") {
      const attrs = (payload?.event ?? {}) as Record<string, string>;
      const created = makeEvent(nextEventId++, attrs.name ?? "New Event", attrs.slug ?? "new-event", "draft");
      created.event_type = (attrs.event_type as EventSummary["event_type"]) ?? "other";
      created.configData.event = { ...created.configData.event, name: created.name, slug: created.slug };
      events.unshift(created);
      return detail(created);
    }
    return events.map(summary);
  }

  if (segments[2] === "events" && segments[3]) {
    const event = findEvent(segments[3]);
    if (!event) throw new DemoNotFound("Event not found");
    const action = segments[4];

    // Saves apply immediately — there is no separate publish-the-config step.
    if (action === "draft" && method === "PATCH") {
      const incoming = (payload?.config ?? {}) as DraftConfig;
      event.configData = { ...event.configData, ...incoming };
      event.draftData = null;
      return { success: true, applied: true };
    }

    // Publish turns the link on.
    if (action === "publish") {
      if (event.draftData) event.configData = { ...event.configData, ...event.draftData };
      event.draftData = null;
      event.status = "published";
      return {
        success: true,
        status: "published",
        slug: event.slug,
        booth_url: `${window.location.origin}/${event.slug}`,
      };
    }

    if (action === "validate_config") return { valid: true };

    if (action === "test_prompt") {
      return { image_url: GENERATED, prompt_used: String(payload?.prompt_template ?? ""), provider: "bfl" };
    }

    if (action === "import_config" || action === "export_config") {
      throw new DemoUnsupported("YAML import/export needs the real API.");
    }

    if (method === "PATCH") {
      const attrs = (payload?.event ?? {}) as Record<string, unknown>;
      Object.assign(event, attrs);
      if (typeof attrs.name === "string") event.configData.event = { ...event.configData.event, name: attrs.name };
      if (typeof attrs.slug === "string") event.configData.event = { ...event.configData.event, slug: attrs.slug };
      return detail(event);
    }

    if (method === "DELETE") {
      event.status = "archived";
      return { message: "Event archived" };
    }

    return detail(event);
  }

  return undefined;
}

export class DemoNotFound extends Error {
  readonly status = 404;
}

export class DemoUnsupported extends Error {
  readonly status = 501;
}
